import { useEffect, useMemo, useState } from 'react';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark-dimmed.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '@/components/ui/Button';
import { ErrorDialog } from '@/components/ui/ErrorDialog';

import { fetchPackageChunks, fetchPackageIndex } from '../api';
import { IndexTreeBrowser } from '../components/IndexTreeBrowser';
import { VersionSwitcher } from '../components/VersionSwitcher';
import { navigateTo } from '../router';
import type { ChunkRecord, IndexNode, LcRouteParams } from '../types';
import {
  buildIndexTreeRoute,
  buildQueryRoute,
  countIndexNodes,
  displayPackageName,
} from '../utils';

interface IndexTreePageProps {
  params: LcRouteParams;
}

const EXT_TO_LANG: Record<string, string> = {
  py: 'python',
  pyi: 'python',
  js: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  jsx: 'javascript',
  yml: 'yaml',
  yaml: 'yaml',
  json: 'json',
  md: 'markdown',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  rs: 'rust',
  go: 'go',
  rb: 'ruby',
  java: 'java',
  c: 'c',
  cpp: 'cpp',
  h: 'c',
  hpp: 'cpp',
  css: 'css',
  html: 'html',
  xml: 'xml',
  sql: 'sql',
  toml: 'ini',
  cfg: 'ini',
  ini: 'ini',
  r: 'r',
};

function getLangFromPath(marker: string): string | undefined {
  // start_marker may be a raw path ("aiohttp/web_exceptions.py") or
  // a markdown heading ("## .github/scripts/docs.sh")
  const cleaned = marker.replace(/^#+\s*/, '');
  const ext = cleaned.split('.').pop()?.toLowerCase();
  return ext ? EXT_TO_LANG[ext] : undefined;
}

function findNode(root: IndexNode, targetId: string | null): IndexNode | null {
  if (!targetId) {
    return null;
  }

  if (root.id === targetId) {
    return root;
  }

  for (const child of root.children) {
    const found = findNode(child, targetId);
    if (found) {
      return found;
    }
  }

  return null;
}

export function IndexTreePage({ params }: IndexTreePageProps) {
  const [root, setRoot] = useState<IndexNode | null>(null);
  const [chunks, setChunks] = useState<ChunkRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const [pageIndex, packageChunks] = await Promise.all([
          fetchPackageIndex(params.registry, params.name, params.version),
          fetchPackageChunks(params.registry, params.name, params.version),
        ]);

        if (isMounted) {
          setRoot(pageIndex);
          setChunks(packageChunks);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unable to load package index.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [params.name, params.registry, params.version]);

  const selectedNode = useMemo(
    () => (root ? findNode(root, selectedId) : null),
    [root, selectedId]
  );
  const selectedChunk = useMemo(
    () =>
      selectedNode?.chunk_id != null
        ? (chunks.find((chunk) => chunk.id === selectedNode.chunk_id) ?? null)
        : null,
    [chunks, selectedNode]
  );

  const highlightedHtml = useMemo(() => {
    if (!selectedChunk || !selectedNode) return null;
    const lang = getLangFromPath(selectedNode.start_marker);
    try {
      if (lang && hljs.getLanguage(lang)) {
        return hljs.highlight(selectedChunk.chunk, { language: lang }).value;
      }
      return hljs.highlightAuto(selectedChunk.chunk).value;
    } catch {
      return null;
    }
  }, [selectedChunk, selectedNode]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink">
            {displayPackageName(params.registry, params.name)}
          </h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-dim">
            <VersionSwitcher params={params} buildRoute={buildIndexTreeRoute} />
            {root ? <span>· {countIndexNodes(root)} nodes</span> : null}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigateTo(buildQueryRoute(params))}>
            Ask
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="lc-surface p-4 text-sm text-dim">Loading page index…</div>
      ) : root ? (
        <div className="grid gap-4 xl:grid-cols-5">
          <div className="xl:col-span-2">
            <IndexTreeBrowser
              root={root}
              selectedId={selectedId}
              onSelect={(node) => setSelectedId(node.id)}
            />
          </div>
          <div className="xl:col-span-3">
            <div className="lc-surface p-5">
              {selectedNode ? (
                <div className="space-y-3">
                  <div>
                    <p className="font-mono text-xs text-dim">{selectedNode.id}</p>
                    <h3 className="mt-1 text-base font-semibold text-ink">{selectedNode.title}</h3>
                  </div>
                  <div className="lc-md-preview text-sm text-link">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {selectedNode.summary}
                    </ReactMarkdown>
                  </div>
                  {selectedChunk ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium uppercase tracking-wider text-dim">
                          Chunk #{selectedChunk.id}
                        </span>
                        <span className="font-mono text-xs text-dim">
                          {selectedNode.start_marker}
                        </span>
                      </div>
                      <div className="lc-code-panel p-4 overflow-auto">
                        {highlightedHtml ? (
                          <pre className="whitespace-pre-wrap break-words font-mono text-sm text-ink">
                            <code
                              className="hljs"
                              dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                            />
                          </pre>
                        ) : (
                          <pre className="whitespace-pre-wrap break-words font-mono text-sm text-ink">
                            {selectedChunk.chunk}
                          </pre>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="lc-code-panel p-4">
                      <pre className="whitespace-pre-wrap font-mono text-sm text-dim">
                        {selectedNode.chunk_id != null
                          ? `Chunk #${selectedNode.chunk_id} is referenced but its content was not found.`
                          : 'No chunk attached to this node.'}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border border-dashed border-rule px-6 py-12 text-center text-sm text-dim">
                  Select a node to preview its content.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {error && (
        <ErrorDialog title="Unable to Load Index" message={error} onClose={() => setError(null)} />
      )}
    </div>
  );
}
